import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GetVideoServices } from '../services/VideoServices'
import { Pagination } from 'antd'
import './base.css'
import './sandbox.css'
import './embla.css'

function Projects() {
  const navigator = useNavigate()

  const [page, setPage] = useState(1)
  const [search] = useState({ text: '' })
  const [loading, setLoading] = useState(false)

  // 🔹 main data
  const [allProjects, setAllProjects] = useState([])
  const [filteredProjects, setFilteredProjects] = useState([])

  // 🔹 filter states
  const [imageTypes, setImageTypes] = useState([])
  const [activeType, setActiveType] = useState('All')

  const [pagination, setPagination] = useState({ total: 0, limit: 12 })

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search?.text])

  async function getData() {
    setLoading(true)
    try {
      const res = await GetVideoServices(page, search?.text)
      const payload = res?.data || {}
      const list = Array.isArray(payload?.datas) ? payload.datas : []

      // ✅ normalize image_type
      const normalizedList = list.map(p => ({
        ...p,
        image_type:
          p?.image_type && p.image_type.trim() !== ''
            ? p.image_type.trim()
            : 'Others'
      }))

      setAllProjects(normalizedList)
      setFilteredProjects(normalizedList)

      const types = [
        'All',
        ...Array.from(new Set(normalizedList.map(p => p.image_type)))
      ]

      setImageTypes(types)
      setPagination(payload?.pagination || { total: normalizedList.length, limit: 12 })
    } catch (err) {
      console.error(err)
      setAllProjects([])
      setFilteredProjects([])
      setPagination({ total: 0, limit: 12 })
    } finally {
      setLoading(false)
    }
  }

  function handleFilter(type) {
    setActiveType(type)

    if (type === 'All') {
      setFilteredProjects(allProjects)
    } else {
      setFilteredProjects(allProjects.filter(p => p.image_type === type))
    }
  }

  const ClientBadge = ({ p }) => {
    const initials =
      (p?.client || '')
        .split(' ')
        .map(s => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase() || 'CL'

    return (
      <div className="absolute z-30 top-4 left-4">
        <div className="flex items-center justify-center w-10 h-10 text-xs font-semibold border rounded-full shadow-md bg-white/90 text-slate-800">
          {initials}
        </div>
      </div>
    )
  }

  const SkeletonCard = () => (
    <div className="animate-pulse bg-gradient-to-br from-slate-100 to-white rounded-2xl p-4 min-h-[220px]">
      <div className="h-40 mb-4 rounded-lg bg-slate-300"></div>
      <div className="w-3/4 h-4 mb-2 rounded bg-slate-300"></div>
      <div className="w-1/2 h-3 rounded bg-slate-300"></div>
    </div>
  )

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px]">
        <div className="z-10">
          <h6 className="text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80">
            Our Projects
          </h6>
          <h6 className="text-center font_normal text-white text-[14px]">
            <span
              onClick={() => navigator('/')}
              className="cursor-pointer hover:underline"
            >
              Home
            </span>{' '}
            / Our Projects
          </h6>
        </div>
        <div className="absolute inset-0 bg-black opacity-60" />
      </div>

      {/* Content */}
      <main className="max-w-[1200px] mx-auto px-4 py-12">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Featured Projects</h2>
          <p className="mt-1 text-sm text-slate-500">
            Explore projects by category
          </p>
        </div>

        {/* 🔘 FILTER BUTTONS (UPDATED UI) */}
        <div className="flex flex-wrap gap-3 mb-12">
          {imageTypes.map(type => (
            <button
              key={type}
              onClick={() => handleFilter(type)}
              className={`px-6 py-2.5 rounded-full text-[14px] font-bold transition-all duration-200
                ${
                  activeType === type
                    ? 'bg-[#027d9b] text-white shadow-md scale-[1.03]'
                    : 'bg-white border border-slate-300 text-black hover:bg-slate-100'
                }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* GRID */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map(p => (
              <article
                key={p._id}
                className="relative overflow-hidden transition duration-300 bg-white shadow-md cursor-pointer group rounded-2xl hover:-translate-y-2"
                onClick={() => navigator(`/projects/detail/${p._id}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  <ClientBadge p={p} />
                  <img
                    src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p.banner}`}
                    alt={p.project_name || p.client}
                    className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-3 left-3 px-3 py-1 text-xs rounded-full bg-[#027d9b] text-white">
                    {p.image_type}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-semibold text-slate-900 line-clamp-2">
                    {p.project_name || 'Untitled Project'}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {p.client}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {pagination?.total > pagination?.limit && (
          <div className="flex justify-center mt-10">
            <Pagination
              size="small"
              current={page}
              total={pagination.total}
              pageSize={pagination.limit}
              showSizeChanger={false}
              onChange={(p) => setPage(p)}
            />
          </div>
        )}
      </main>
    </div>
  )
}

export default Projects







//=======>> old one 


// import React, { useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import './base.css'
// import './sandbox.css'
// import './embla.css'

// function Projects() {
//   const navigator = useNavigate()

//   const [page, setPage] = useState(1)
//   const [search] = useState({ text: '' })
//   const [loading, setLoading] = useState(false)

//   // 🔹 main data
//   const [allProjects, setAllProjects] = useState([])
//   const [filteredProjects, setFilteredProjects] = useState([])

//   // 🔹 filter states
//   const [imageTypes, setImageTypes] = useState([])
//   const [activeType, setActiveType] = useState('All')

//   const [pagination, setPagination] = useState({ total: 0, limit: 12 })

//   useEffect(() => {
//     getData()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [page, search?.text])

//   async function getData() {
//     setLoading(true)
//     try {
//       const res = await GetVideoServices(page, search?.text)
//       const payload = res?.data || {}
//       const list = Array.isArray(payload?.datas) ? payload.datas : []

//       // ✅ NORMALIZE image_type (IMPORTANT FIX)
//       const normalizedList = list.map(p => ({
//         ...p,
//         image_type:
//           p?.image_type && p.image_type.trim() !== ''
//             ? p.image_type.trim()
//             : 'Others'
//       }))

//       setAllProjects(normalizedList)
//       setFilteredProjects(normalizedList)

//       // ✅ extract unique image types
//       const types = [
//         'All',
//         ...Array.from(new Set(normalizedList.map(p => p.image_type)))
//       ]

//       setImageTypes(types)
//       setPagination(payload?.pagination || { total: normalizedList.length, limit: 12 })
//     } catch (err) {
//       console.error(err)
//       setAllProjects([])
//       setFilteredProjects([])
//       setPagination({ total: 0, limit: 12 })
//     } finally {
//       setLoading(false)
//     }
//   }

//   // 🔹 filter handler
//   function handleFilter(type) {
//     setActiveType(type)

//     if (type === 'All') {
//       setFilteredProjects(allProjects)
//     } else {
//       setFilteredProjects(
//         allProjects.filter(p => p.image_type === type)
//       )
//     }
//   }

//   // 🔹 client badge
//   const ClientBadge = ({ p }) => {
//     const initials =
//       (p?.client || '')
//         .split(' ')
//         .map(s => s[0])
//         .slice(0, 2)
//         .join('')
//         .toUpperCase() || 'CL'

//     return (
//       <div className="absolute z-30 top-4 left-4">
//         <div className="flex items-center justify-center w-10 h-10 text-xs font-semibold border rounded-full shadow-md bg-white/90 text-slate-800">
//           {initials}
//         </div>
//       </div>
//     )
//   }

//   // 🔹 skeleton loader
//   const SkeletonCard = () => (
//     <div className="animate-pulse bg-gradient-to-br from-slate-100 to-white rounded-2xl p-4 min-h-[220px]">
//       <div className="h-40 mb-4 rounded-lg bg-slate-300"></div>
//       <div className="w-3/4 h-4 mb-2 rounded bg-slate-300"></div>
//       <div className="w-1/2 h-3 rounded bg-slate-300"></div>
//     </div>
//   )

//   return (
//     <div className="min-h-screen">
//       {/* Hero */}
//       <div className="relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px]">
//         <div className="z-10">
//           <h6 className="text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80">
//             Our Projects
//           </h6>
//           <h6 className="text-center font_normal text-white text-[14px]">
//             <span
//               onClick={() => navigator('/')}
//               className="cursor-pointer hover:underline"
//             >
//               Home
//             </span>{' '}
//             / Our Projects
//           </h6>
//         </div>
//         <div className="absolute inset-0 bg-black opacity-60" />
//       </div>

//       {/* Content */}
//       <main className="max-w-[1200px] mx-auto px-4 py-12">
//         <div className="mb-6">
//           <h2 className="text-2xl font-bold text-slate-900">Featured Projects</h2>
//           <p className="mt-1 text-sm text-slate-500">
//             Explore projects by category
//           </p>
//         </div>

//         {/* 🔘 FILTER BUTTONS */}
//         <div className="flex flex-wrap gap-2 mb-10">
//           {imageTypes.map(type => (
//             <button
//               key={type}
//               onClick={() => handleFilter(type)}
//               className={`px-4 py-1.5 rounded-full text-sm font-medium transition
//                 ${
//                   activeType === type
//                     ? 'bg-[#027d9b] text-white shadow'
//                     : 'bg-white border text-slate-700 hover:bg-slate-100'
//                 }`}
//             >
//               {type}
//             </button>
//           ))}
//         </div>

//         {/* GRID */}
//         {loading ? (
//           <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//             {Array.from({ length: 6 }).map((_, i) => (
//               <SkeletonCard key={i} />
//             ))}
//           </div>
//         ) : (
//           <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//             {filteredProjects.map(p => (
//               <article
//                 key={p._id}
//                 className="relative overflow-hidden transition duration-300 bg-white shadow-md cursor-pointer group rounded-2xl hover:-translate-y-2"
//                 onClick={() => navigator(`/projects/detail/${p._id}`)}
//               >
//                 <div className="relative h-64 overflow-hidden">
//                   <ClientBadge p={p} />
//                   <img
//                     src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p.banner}`}
//                     alt={p.project_name || p.client}
//                     className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
//                   />
//                   <div className="absolute bottom-3 left-3 px-3 py-1 text-xs rounded-full bg-[#027d9b] text-white">
//                     {p.image_type}
//                   </div>
//                 </div>

//                 <div className="p-4">
//                   <h3 className="text-lg font-semibold text-slate-900 line-clamp-2">
//                     {p.project_name || 'Untitled Project'}
//                   </h3>
//                   <p className="mt-1 text-sm text-slate-500">
//                     {p.client}
//                   </p>
//                 </div>
//               </article>
//             ))}
//           </div>
//         )}

//         {/* PAGINATION */}
//         {pagination?.total > pagination?.limit && (
//           <div className="flex justify-center mt-10">
//             <Pagination
//               size="small"
//               current={page}
//               total={pagination.total}
//               pageSize={pagination.limit}
//               showSizeChanger={false}
//               onChange={(p) => setPage(p)}
//             />
//           </div>
//         )}
//       </main>
//     </div>
//   )
// }

// export default Projects









//=======>> Old one 





// import React, { useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import './base.css'
// import './sandbox.css'
// import './embla.css'

// function Projects() {
//   const navigator = useNavigate()

//   const [page, setPage] = useState(1)
//   const [search] = useState({ text: '' })
//   const [loading, setLoading] = useState(false)

//   // 🔹 main data
//   const [allProjects, setAllProjects] = useState([])
//   const [filteredProjects, setFilteredProjects] = useState([])

//   // 🔹 filter states
//   const [imageTypes, setImageTypes] = useState([])
//   const [activeType, setActiveType] = useState('All')

//   const [pagination, setPagination] = useState({ total: 0, limit: 12 })

//   useEffect(() => {
//     getData()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [page, search?.text])

//   async function getData() {
//     setLoading(true)
//     try {
//       const res = await GetVideoServices(page, search?.text)
//       const payload = res?.data || {}
//       const list = Array.isArray(payload?.datas) ? payload.datas : []

//       // store full list
//       setAllProjects(list)
//       setFilteredProjects(list)

//       // 🔹 extract unique image types
//       const types = [
//         'All',
//         ...Array.from(
//           new Set(
//             list
//               .map(p => p.image_type?.trim())
//               .filter(Boolean)
//           )
//         )
//       ]

//       setImageTypes(types)
//       setPagination(payload?.pagination || { total: list.length, limit: 12 })
//     } catch (err) {
//       console.error(err)
//       setAllProjects([])
//       setFilteredProjects([])
//       setPagination({ total: 0, limit: 12 })
//     } finally {
//       setLoading(false)
//     }
//   }

//   // 🔹 filter handler
//   function handleFilter(type) {
//     setActiveType(type)

//     if (type === 'All') {
//       setFilteredProjects(allProjects)
//     } else {
//       setFilteredProjects(
//         allProjects.filter(
//           p => p?.image_type?.trim() === type
//         )
//       )
//     }
//   }

//   // 🔹 client badge
//   const ClientBadge = ({ p }) => {
//     const initials =
//       (p?.client || '')
//         .split(' ')
//         .map(s => s[0])
//         .slice(0, 2)
//         .join('')
//         .toUpperCase() || 'CL'

//     return (
//       <div className="absolute z-30 top-4 left-4">
//         <div className="flex items-center justify-center w-10 h-10 text-xs font-semibold border rounded-full shadow-md bg-white/90 text-slate-800">
//           {initials}
//         </div>
//       </div>
//     )
//   }

//   // 🔹 skeleton loader
//   const SkeletonCard = () => (
//     <div className="animate-pulse bg-gradient-to-br from-slate-100 to-white rounded-2xl p-4 min-h-[220px]">
//       <div className="h-40 mb-4 rounded-lg bg-slate-300"></div>
//       <div className="w-3/4 h-4 mb-2 rounded bg-slate-300"></div>
//       <div className="w-1/2 h-3 rounded bg-slate-300"></div>
//     </div>
//   )

//   return (
//     <div className="min-h-screen">
//       {/* Hero */}
//       <div className="relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px]">
//         <div className="z-10">
//           <h6 className="text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80">
//             Our Projects
//           </h6>
//           <h6 className="text-center font_normal text-white text-[14px]">
//             <span
//               onClick={() => navigator('/')}
//               className="cursor-pointer hover:underline"
//             >
//               Home
//             </span>{' '}
//             / Our Projects
//           </h6>
//         </div>
//         <div className="absolute inset-0 bg-black opacity-60" />
//       </div>

//       {/* Content */}
//       <main className="max-w-[1200px] mx-auto px-4 py-12">
//         <div className="mb-6">
//           <h2 className="text-2xl font-bold text-slate-900">Featured Projects</h2>
//           <p className="mt-1 text-sm text-slate-500">
//             Explore projects by category
//           </p>
//         </div>

//         {/* 🔘 FILTER BUTTONS */}
//         <div className="flex flex-wrap gap-2 mb-10">
//           {imageTypes.map(type => (
//             <button
//               key={type}
//               onClick={() => handleFilter(type)}
//               className={`px-4 py-1.5 rounded-full text-sm font-medium transition
//                 ${
//                   activeType === type
//                     ? 'bg-[#027d9b] text-white shadow'
//                     : 'bg-white border text-slate-700 hover:bg-slate-100'
//                 }`}
//             >
//               {type}
//             </button>
//           ))}
//         </div>

//         {/* GRID */}
//         {loading ? (
//           <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//             {Array.from({ length: 6 }).map((_, i) => (
//               <SkeletonCard key={i} />
//             ))}
//           </div>
//         ) : (
//           <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//             {filteredProjects.map(p => (
//               <article
//                 key={p._id}
//                 className="relative overflow-hidden transition duration-300 bg-white shadow-md group rounded-2xl hover:-translate-y-2"
//                 onClick={() => navigator(`/projects/detail/${p._id}`)}
//               >
//                 <div className="relative h-64 overflow-hidden">
//                   <ClientBadge p={p} />
//                   <img
//                     src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p.banner}`}
//                     alt={p.project_name || p.client}
//                     className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
//                   />
//                   <div className="absolute bottom-3 left-3 px-3 py-1 text-xs rounded-full bg-[#027d9b] text-white">
//                     {p.image_type}
//                   </div>
//                 </div>

//                 <div className="p-4">
//                   <h3 className="text-lg font-semibold text-slate-900 line-clamp-2">
//                     {p.project_name || 'Untitled Project'}
//                   </h3>
//                   <p className="mt-1 text-sm text-slate-500">
//                     {p.client}
//                   </p>
//                 </div>
//               </article>
//             ))}
//           </div>
//         )}

//         {/* PAGINATION */}
//         {pagination?.total > pagination?.limit && (
//           <div className="flex justify-center mt-10">
//             <Pagination
//               size="small"
//               current={page}
//               total={pagination.total}
//               pageSize={pagination.limit}
//               showSizeChanger={false}
//               onChange={(p) => setPage(p)}
//             />
//           </div>
//         )}
//       </main>
//     </div>
//   )
// }

// export default Projects










//========>> Old 


// src/pages/Projects.jsx
// import React, { useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import './base.css'
// import './sandbox.css'
// import './embla.css'

// /**
//  * Modern / Z-Gen trending Projects UI
//  * - Uses CSS Grid (responsive)
//  * - Glassmorphism card, smooth hover, micro-interactions
//  * - Works with existing data: datas.datas and datas.pagination
//  */

// function Projects() {
//   const navigator = useNavigate()
//   const [page, setPage] = useState(1)
//   const [search] = useState({ text: '' })
//   const [loading, setLoading] = useState(false)

//   // safe default shape
//   const [datas, setDatas] = useState({ datas: [], pagination: { total: 0, limit: 12 } })

//   useEffect(() => {
//     getData()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [page, search?.text])

//   async function getData() {
//     setLoading(true)
//     try {
//       const res = await GetVideoServices(page, search?.text)
//       const payload = res?.data || {}
//       setDatas({
//         datas: Array.isArray(payload?.datas) ? payload.datas : (payload?.datas || []),
//         pagination: payload?.pagination || { total: payload?.datas?.length || 0, limit: payload?.limit || 12 }
//       })
//     } catch (err) {
//       console.error('GetVideoServices error', err)
//       setDatas({ datas: [], pagination: { total: 0, limit: 12 } })
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Avatar / badge: show logo if available else initials
//   const ClientBadge = ({ p }) => {
//     const logo = p?.client_logo // adjust field name if different
//     const initials = (p?.client || '').split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase() || 'CL'
//     return (
//       <div className="absolute z-30 top-4 left-4">
//         {logo ? (
//           <img
//             src={`${process.env.REACT_APP_AWS_IMAGE_URL}${logo}`}
//             alt={`${p?.client} logo`}
//             className="object-cover w-10 h-10 border-2 border-white rounded-full shadow-md"
//             loading="lazy"
//           />
//         ) : (
//           <div className="flex items-center justify-center w-10 h-10 text-xs font-semibold border rounded-full shadow-md bg-white/90 text-slate-800">
//             {initials}
//           </div>
//         )}
//       </div>
//     )
//   }

//   // Skeleton card shown when loading
//   const SkeletonCard = () => (
//     <div className="animate-pulse bg-gradient-to-br from-slate-100 to-white rounded-2xl p-4 min-h-[220px]">
//       <div className="h-40 mb-4 rounded-lg bg-slate-300"></div>
//       <div className="w-3/4 h-4 mb-2 rounded bg-slate-300"></div>
//       <div className="w-1/2 h-3 rounded bg-slate-300"></div>
//     </div>
//   )
//   const totalProjects = datas?.pagination?.total || datas?.datas?.length || 0
//   return (
//     <div className="min-h-screen">
//       {/* Hero */}
//       <div className='relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px] 2xl:h-[500px]'>
//         <div className='z-10'>
//           <h6 className='text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80 2xl:text-[35px]'>Our Projects</h6>
//           <h6 className='text-center font_normal text-white opacity-100 text-[14px] 2xl:text-[18px]'>
//             <span onClick={() => navigator('/')} className='relative cursor-pointer after:bg-[#fff] after:absolute after:h-[2px] after:w-0 after:bottom-0 after:left-0 text-white opacity-100 hover:after:w-full after:transition-all after:duration-300'>Home</span> / Our Projects
//           </h6>
//           <h6 className='text-[13px] mt-1 text-white text-center font_normal opacity-80 2xl:text-[16px]'>Take a moment to explore our completed projects.</h6>
//         </div>
//         <div className='bg-black opacity-60 absolute top-0 left-0 w-full h-[200px] md:h-[340px] 2xl:h-[500px]'></div>
//       </div>

//       {/* Container */}
//       <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-12">
//         <div className="flex items-center justify-between mb-6">
//           <div>
//             <h2 className="text-lg font-bold md:text-2xl text-slate-900">Featured Projects</h2>
//             <p className="mt-1 text-sm text-slate-500">A handpicked selection of our recent works.</p>
//           </div>
//           <div className="flex items-center gap-3">
//             <span className="inline-flex items-center px-3 py-1 text-xs bg-white border rounded-full shadow-sm border-slate-200 text-slate-700">
//               <span className="mr-2 h-1.5 w-1.5 rounded-full bg-emerald-500" />
//               {totalProjects} projects
//             </span>
//           </div>
//         </div>

//         {/* Grid: modern responsive layout */}
//         <section aria-live="polite">
//           {loading ? (
//             <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//               {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
//             </div>
//           ) : (
//             <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
//               {datas?.datas?.map((p) => (
//      <article
//      key={p?._id || p?.banner || Math.random()}
//      className="relative group rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgba(2,13,44,0.06)] transform transition will-change-transform duration-300 hover:-translate-y-2"
//      onClick={() => navigator(`/projects/detail/${p?._id}`)}
//      role="button"
//      tabIndex={0}
//      onKeyDown={(e) => { if (e.key === 'Enter') navigator(`/projects/detail/${p?._id}`) }}
//    >
//      {/* Image frame */}
//      <div className="relative w-full h-64 overflow-hidden md:h-72 lg:h-80">
//        <ClientBadge p={p} />
   
//        <img
//          src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`}
//          alt={p?.project_name || p?.client}
//          loading="lazy"
//          className="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
//        />
   
//        <div className="absolute left-0 bottom-0 ml-4 mb-4 px-3 py-1 rounded-full bg-gradient-to-r from-[#027d9b] to-[#00b4d8] text-white text-xs font-semibold shadow-lg">
//          {p?.status || 'Completed'}
//        </div>
   
//        <div className="absolute z-30 flex flex-col gap-2 top-3 right-3">
//          {p?.total_sqft && (
//            <span className="bg-white/90 text-slate-900 text-[11px] px-2 py-1 rounded-full font-medium shadow-sm">
//              {`${p.total_sqft} sqft`}
//            </span>
//          )}
//          {p?.category && (
//            <span className="bg-white/90 text-slate-900 text-[11px] px-2 py-1 rounded-full font-medium shadow-sm">
//              {p.category}
//            </span>
//          )}
//        </div>
   
//        <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-t from-black/40 to-transparent group-hover:opacity-100"></div>
//      </div>
   
//      {/* Content (reduced height) */}
//      <div className="p-3 md:p-4">
//        <h3 className="mb-1 font-semibold text-md md:text-lg text-slate-900 line-clamp-2">{p?.project_name || 'Untitled Project'}</h3>
//        <p className="mb-3 text-sm text-slate-500">{p?.client || 'Unknown Client'}</p>
   
//        <div className="flex items-center justify-between">
//          <p className="text-sm text-slate-500 line-clamp-2">{p?.short_description || p?.subtitle || ''}</p>
//          <button
//            onClick={(e) => { e.stopPropagation(); navigator(`/projects/detail/${p?._id}`) }}
//            className="ml-4 inline-flex items-center gap-2 bg-gradient-to-r from-[#027d9b] to-[#00b4d8] text-white px-3 py-1.5 rounded-full text-sm font-medium shadow hover:scale-[1.02] transition-transform duration-200"
//            aria-label={`View ${p?.project_name}`}
//          >
//            View
//            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
//            </svg>
//          </button>
//        </div>
//      </div>
//    </article>
   
//               ))}
//             </div>
//           )}
//         </section>

//         {/* Pagination */}
//         {(datas?.pagination?.total > datas?.pagination?.limit) && (
//           <div className="flex justify-center mt-8">
//             <Pagination
//               size="small"
//               onChange={(v) => setPage(v)}
//               current={page}
//               total={datas?.pagination?.total}
//               pageSize={datas?.pagination?.limit}
//               showSizeChanger={false}
//             />
//           </div>
//         )}
//       </main>
//     </div>
//   )
// }

// export default Projects








////=========>> Old good 


// // src/pages/Projects.jsx
// import React, { useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
// import './base.css'
// import './sandbox.css'
// import './embla.css'

// function Projects() {
//   const navigator = useNavigate()
//   const [page, setPage] = useState(1)
//   const [search] = useState({ text: '' })

//   // safe default shape
//   const [datas, setDatas] = useState({ datas: [], pagination: { total: 0, limit: 12 } })
//   const [loading, setLoading] = useState(false)

//   useEffect(() => {
//     getData()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [page, search?.text])

//   async function getData() {
//     setLoading(true)
//     try {
//       const response = await GetVideoServices(page, search?.text)
//       console.log('GetVideoServices response:', response)
//       // If response.data is an array directly, normalize it. Here we assume shape { datas: [], pagination: {} }
//       const res = response?.data || {}
//       setDatas({
//         datas: Array.isArray(res?.datas) ? res.datas : (res?.datas || []),
//         pagination: res?.pagination || { total: res?.datas?.length || 0, limit: res?.limit || 12 }
//       })
//     } catch (err) {
//       console.error('Failed to fetch projects', err)
//       setDatas({ datas: [], pagination: { total: 0, limit: 12 } })
//     } finally {
//       setLoading(false)
//     }
//   }

//   // small utility to render client badge (logo if available, else initials)
//   const ClientBadge = ({ p }) => {
//     const logo = p?.client_logo // update if your payload has logo url
//     const initials = (p?.client || '').split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase()
//     return (
//       <div className="absolute z-20 top-3 left-3">
//         {logo ? (
//           <img src={`${process.env.REACT_APP_AWS_IMAGE_URL}${logo}`} alt={`${p?.client} logo`} className="object-cover w-10 h-10 border-2 border-white rounded-full shadow" loading="lazy" />
//         ) : (
//           <div className="flex items-center justify-center w-10 h-10 text-xs font-semibold text-gray-800 border rounded-full shadow bg-white/90">
//             {initials || 'CL'}
//           </div>
//         )}
//       </div>
//     )
//   }

//   return (
//     <div>
//       {/* Hero */}
//       <div className='relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px] 2xl:h-[500px]'>
//         <div className='z-10 px-4 text-center'>
//           <h1 className='text-[28px] md:text-[38px] text-white tracking-[6px] font_thicker opacity-90'>Our Projects</h1>
//           <p className='mt-2 text-[14px] md:text-[16px] text-white opacity-90'>A curated showcase of our completed works.</p>
//         </div>
//         <div className='absolute inset-0 bg-black opacity-60'></div>
//       </div>

//       {/* Container */}
//       <div className='w-[92%] md:w-[80%] mx-auto my-[5%]'>
//         <div className='flex items-center justify-between mb-6'>
//           <h2 className='text-[18px] md:text-[20px] font-semibold'>Featured Projects</h2>
//           <p className='text-sm text-gray-600'>{datas?.pagination?.total || datas?.datas?.length} Projects</p>
//         </div>

//         {/* Loading indicator */}
//         {loading && (
//           <div className="flex items-center justify-center w-full py-12">
//             <div className="text-gray-500 animate-pulse">Loading projects...</div>
//           </div>
//         )}

//         {/* Masonry grid */}
//         {!loading && (
//           <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 1100: 3 }}>
//             <Masonry gutter="16px">
//               {datas?.datas?.map((p) => (
//                 <article
//                   key={p?._id || p?.banner || Math.random()}
//                   className="relative group rounded-2xl overflow-hidden shadow-lg bg-white transition-transform duration-300 hover:scale-[1.02] cursor-pointer"
//                   onClick={() => navigator(`/projects/detail/${p?._id}`)}
//                   aria-label={p?.project_name || p?.client}
//                 >
//                   {/* Image area with consistent aspect ratio */}
//                   <div className="relative w-full" style={{ paddingBottom: '66.66%' /* 3:2 ratio */ }}>
//                     <ClientBadge p={p} />

//                     <img
//                       src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`}
//                       alt={p?.project_name || p?.client}
//                       loading="lazy"
//                       className="absolute inset-0 object-cover w-full h-full"
//                     />

//                     {/* overlay gradient bottom */}
//                     <div className="absolute inset-x-0 bottom-0 transition-opacity duration-300 h-1/2 bg-gradient-to-t from-black/70 to-transparent opacity-90 group-hover:opacity-100"></div>

//                     {/* quick meta chips top-right */}
//                     <div className="absolute z-20 flex flex-col gap-2 top-3 right-3">
//                       {p?.total_sqft ? (
//                         <span className="text-[11px] bg-white/90 text-gray-900 px-2 py-1 rounded-full font-medium shadow">{`${p?.total_sqft} sqft`}</span>
//                       ) : null}
//                       {p?.category && (
//                         <span className="text-[11px] bg-white/90 text-gray-900 px-2 py-1 rounded-full font-medium shadow">{p?.category}</span>
//                       )}
//                     </div>
//                   </div>

//                   {/* Details area */}
//                   <div className="p-4">
//                     <h3 className="text-[16px] md:text-[18px] font-bold text-gray-900 leading-snug">{p?.project_name || 'Untitled Project'}</h3>
//                     <p className="text-[13px] text-gray-600 mt-1">{p?.client || 'Unknown Client'}</p>

//                     {/* short description or CTA */}
//                     <div className="flex items-center justify-between mt-3">
//                       <span className="text-sm text-gray-500 line-clamp-2">{p?.short_description || p?.subtitle || ''}</span>
//                       <button
//                         onClick={(e) => { e.stopPropagation(); navigator(`/projects/detail/${p?._id}`) }}
//                         className="ml-4 text-sm bg-[#027d9b] text-white px-3 py-1 rounded-full shadow hover:brightness-95"
//                       >
//                         View
//                       </button>
//                     </div>
//                   </div>
//                 </article>
//               ))}
//             </Masonry>
//           </ResponsiveMasonry>
//         )}

//         {/* Pagination */}
//         {(datas?.pagination?.total > datas?.pagination?.limit) && (
//           <div className='flex items-center justify-center w-full mx-auto my-8'>
//             <Pagination
//               size='small'
//               onChange={(v) => setPage(v)}
//               current={page}
//               total={datas?.pagination?.total}
//               pageSize={datas?.pagination?.limit}
//               showSizeChanger={false}
//             />
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default Projects









//==========>>>





// // src/pages/Projects.jsx  (or where it lives)
// import React, { useEffect, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import Masonry, { ResponsiveMasonry } from "react-responsive-masonry"
// import useEmblaCarousel from 'embla-carousel-react'
// import './base.css'
// import './sandbox.css'
// import './embla.css'

// function Projects() {
//   const [emblaRef] = useEmblaCarousel()
//   const navigator = useNavigate()
//   const [page, setpage] = useState(1)
//   const [search, setsearch] = useState({ text: '' })

//   // give a safe default shape
//   const [datas, setdatas] = useState({ datas: [], pagination: { total: 0, limit: 10 } })

//   useEffect(() => {
//     getData()
//   }, [page, search?.text]) // refetch when search text changes too

//   async function getData() {
//     try {
//       const response = await GetVideoServices(page, search?.text)
//       console.log('GetVideoServices response:', response) // debug - remove in prod
//       // assume response.data has shape { datas: [...], pagination: {...} }
//       setdatas({ ...response?.data })
//     } catch (err) {
//       console.error('Failed to fetch projects', err)
//       // optionally setdatas to empty safe state or show error UI
//       setdatas({ datas: [], pagination: { total: 0, limit: 10 } })
//     }
//   }

//   return (
//     <div>
//       <div className='relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px] md:h-[340px] 2xl:h-[500px]'>
//         <div className='z-10'>
//           <h6 className='text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80 2xl:text-[35px]'>Our Projects</h6>
//           <h6 className='text-center font_normal text-white opacity-100 text-[14px] 2xl:text-[18px]'>
//             <span onClick={() => navigator('/')} className='relative cursor-pointer after:bg-[#fff] after:absolute after:h-[2px] after:w-0 after:bottom-0 after:left-0 text-white opacity-100 hover:after:w-full after:transition-all after:duration-300'>Home</span> / Our Projects
//           </h6>
//           <h6 className='text-[13px] mt-1 text-white text-center font_normal opacity-80 2xl:text-[16px]'>Take a moment to explore our completed projects.</h6>
//         </div>
//         <div className='bg-black opacity-60 absolute top-0 left-0 w-full h-[200px] md:h-[340px] 2xl:h-[500px]'></div>
//       </div>

//       <div className='w-[90%] md:w-[80%] mx-auto my-[5%]'>
//         <h6 data-aos="fade-left" className='font_normal text-[15px] 2xl:text-[18px] my-10'>
//           We have brought our <span className='font-[800] font_bold'>client's dream</span> to life through our dedicated efforts and successful project completions.
//         </h6>

//         <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
//           <Masonry gutter="10px">
//             {datas?.datas?.map((p) => (
//               <div
//                 key={p?._id || Math.random()} // ensure a key; prefer _id
//                 className='relative overflow-hidden cursor-pointer group'
//                 onClick={() => navigator(`/projects/detail/${p?._id}`)}
//                 style={{ marginBottom: 10 }}
//               >
//                 <img
//                   src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`}
//                   alt={p?.project_name || p?.client}
//                   className='object-cover w-full h-auto'
//                 />

//                 <div className='absolute bottom-0 hidden min-w-full min-h-full bg-black/30 group-hover:block'>
//                   <div className='text-white transition-all duration-300'>
//                     <div className='p-2 mt-2'>
//                       <h6 className='font_bold text-[15px] uppercase ml-4 w-[90%]'>{p?.client}</h6>
//                       <h6 className='font_normal text-[13px] ml-4 mt-2 w-[90%]'>{p?.project_name} {p?.total_sqft !== '' && `/ Total SQFT - ${p?.total_sqft}`}</h6>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </Masonry>
//         </ResponsiveMasonry>

//         {(datas?.pagination?.total > datas?.pagination?.limit) && (
//           <div className='mx-auto flex items-center justify-center my-[5%] w-full'>
//             <Pagination
//               size='small'
//               onChange={(v) => setpage(v)}
//               current={page}
//               total={datas?.pagination?.total}
//               pageSize={datas?.pagination?.limit}
//             />
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default Projects









//==========>> Old one  decemebr update 



// import React, { useEffect, useState } from 'react'
// import { projectArr } from '../constants/AppConstant'
// import { useNavigate } from 'react-router-dom'
// import { GetVideoServices } from '../services/VideoServices'
// import { Pagination } from 'antd'
// import { MasonryGrid,} from "@egjs/react-grid";
// import Masonry, {ResponsiveMasonry} from "react-responsive-masonry"
// import useEmblaCarousel from 'embla-carousel-react'
// import { Swiper, SwiperSlide } from 'swiper/react';
// import 'swiper/css';
// import EmblaCarousel from '../components/carouselAnimate/EmblaCarousel'
// // import 'swiper/css/navigation';
// // import 'swiper/css/pagination';
// // import 'swiper/css/scrollbar';

// import './base.css'
// import './sandbox.css'
// import './embla.css'

// function Projects() {

//   const [emblaRef] = useEmblaCarousel()
//   const navigator = useNavigate()
//   const [page,setpage] = useState(1)
//   const [search,setsearch] = useState({text:''})

//   const [datas,setdatas] = useState({})

//   useEffect(()=>{
//     getData()
//   },[page])

//   async function getData(){
//     const response = await GetVideoServices(page,search?.text)
//     setdatas({...response?.data,datas:response?.data?.datas})
//   }


//   const OPTIONS = { dragFree: true, loop: true }
//   const SLIDE_COUNT = 5
//   const SLIDES = Array.from(Array(SLIDE_COUNT).keys())

  
//   return (
//     <div>
//         <div className='relative flex items-center justify-center bg-projects_bg2 bg-center bg-cover h-[200px]  md:h-[340px] 2xl:h-[500px]'>
//             <div className='z-10'>
//               <h6 className='text-[30px] text-white text-center tracking-[10px] font_thicker opacity-80 2xl:text-[35px]'>Our Projects</h6>
//               <h6 className='text-center font_normal text-white opacity-100 text-[14px] 2xl:text-[18px]'> <span onClick={()=>navigator('/')} className='relative cursor-pointer after:bg-[#fff] after:absolute after:h-[2px] after:w-0 after:bottom-0 after:left-0  text-white opacity-100  hover:after:w-full after:transition-all after:duration-300'>Home</span> / Our Projects </h6>
//               <h6 className='text-[13px] mt-1  text-white text-center font_normal opacity-80 2xl:text-[16px]'>Take a moment  to explore our completed projects. </h6>
//             </div>
//             <div className='bg-black opacity-60 absolute top-0 left-0 w-full h-[200px]  md:h-[340px]  2xl:h-[500px]'>
//             </div> 
//          </div>


         
//          <div className='w-[90%] md:w-[80%] mx-auto my-[5%]'>
//            <h6 data-aos="fade-left" className='font_normal text-[15px]  2xl:text-[18px] my-10'>We have brought our  <span className='font-[800] font_bold'>client's dream</span> to life through our dedicated efforts and succeesful project completions.   </h6>


//             {/* <div className='grid grid-cols-1 gap-5 md:grid-cols-4'>

//               {datas?.datas?.map((p)=>(
//                   <div className='group cursor-pointer h-[400px] md:h-[400px] overflow-hidden' onClick={()=>navigator(`/projects/detail/${p?._id}`)}>
//                     <img src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`} className='h-[400px] md:h-[400px] w-[100%] object-cover' /> 

//                     <div className='inline-block bg-black/30 duration-300 transition-all group-hover:-translate-y-[400px] h-[400px] min-w-[100%] min-height-[100%] '>
//                       <div className='transition-all duration-300 text-[#fff] '>
//                         <div className='p-2 mt-2 '>  
//                           <div>
//                           <h6 className='font_bold text-[20px] uppercase ml-4 w-[90%]'>{p?.client}</h6>
//                           <h6 className='font_normal text-[14px] ml-4 mt-4 w-[90%]'>{p?.project_name}  {p?.total_sqft !== '' &&  `/ Total SQFT - ${p?.total_sqft}`}</h6>
//                           </div>
//                         </div>
//                       </div>
//                   </div> 

//                     <div>

//                     </div>  
//                   </div>  
//                 ))
//               }

//             </div> */}

//             {/* <MasonryGrid
//               className="container"
//               gap={5}
//               defaultDirection={"end"}
//               align={"justify"}
//             > */}

// <ResponsiveMasonry
//                 columnsCountBreakPoints={{350: 1, 750: 2, 900: 3}}
//             >
              
//               <Masonry gutter={10} columnsCount={2}>
//               {datas?.datas?.slice(0,5)?.map((p)=>(
//                   <div className='relative overflow-hidden cursor-pointer group' onClick={()=>navigator(`/projects/detail/${p?._id}`)}>
//                     <img src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`} className='w-[100%] h-[100%] object-cover' /> 
//                     {/* <h1 className='text-[20px] bg-white p-4 absolute top-2 left-2'>{p?.sort}</h1> */}
//                     <div className='bg-black/30 min-w-[100%] min-height-[100%] hidden absolute group-hover:block bottom-0'>
//                       <div className='transition-all duration-300 text-[#fff] '>
//                         <div className='p-2 mt-2'>  
//                           <div>
//                           <h6 className='font_bold text-[15px] uppercase ml-4 w-[90%]'>{p?.client}</h6>
//                           <h6 className='font_normal text-[13px] ml-4 mt-2 w-[90%]'>{p?.project_name}  {p?.total_sqft !== '' &&  `/ Total SQFT - ${p?.total_sqft}`}</h6>
//                           </div>
//                         </div>
//                       </div>
//                   </div> 

//                     <div>

//                     </div>  
//                   </div>  
//                 ))
//               }
//               </Masonry>
//               </ResponsiveMasonry>

//               {/* <EmblaCarousel slides={SLIDES} options={OPTIONS} /> */}

//               {/* <Swiper
//                 // modules={[Pagination]}
//                 spaceBetween={50}
//                 slidesPerView={3}
//                 navigation
//                 pagination={{ clickable: true }}
//                 scrollbar={{ draggable: true }}
//                 onSwiper={(swiper) => console.log(swiper)}
//                 onSlideChange={() => console.log('slide change')}
//               >

//                 {datas?.datas?.map((p)=>(
//                 <SwiperSlide>
//                   <div className='group cursor-pointer h-[400px] md:h-[400px] overflow-hidden' onClick={()=>navigator(`/projects/detail/${p?._id}`)}>
//                     <img src={`${process.env.REACT_APP_AWS_IMAGE_URL}${p?.banner}`} className='h-[400px] md:h-[400px] w-[100%] object-cover' /> 

//                     <div className='inline-block bg-black/30 duration-300 transition-all group-hover:-translate-y-[400px] h-[400px] min-w-[100%] min-height-[100%] '>
//                       <div className='transition-all duration-300 text-[#fff] '>
//                         <div className='p-2 mt-2 '>  
//                           <div>
//                           <h6 className='font_bold text-[20px] uppercase ml-4 w-[90%]'>{p?.client}</h6>
//                           <h6 className='font_normal text-[14px] ml-4 mt-4 w-[90%]'>{p?.project_name}  {p?.total_sqft !== '' &&  `/ Total SQFT - ${p?.total_sqft}`}</h6>
//                           </div>
//                         </div>
//                       </div>
//                   </div> 
//                     <div>

//                     </div>  
//                   </div>  
//                   </SwiperSlide>
//                 ))
//               }

//               </Swiper>   */}

              
//             {/* </MasonryGrid> */}


            
//             {(datas?.pagination?.total > datas?.pagination?.limit) &&
//             <div className='mx-auto flex items-center justify-center my-[5%] w-full'>
//             <Pagination size='small' onChange={(v)=>setpage(v)} total={datas?.pagination?.total} pageSize={datas?.pagination?.limit} />
//             </div>}
//          </div>
//     </div>

//   )
// }

// export default Projects